import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import '../styles.css';

function Search() {
  const [songResults, setSongResults] = useState([]);
  const [postResults, setPostResults] = useState([]);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = (searchParams.get('query') || '').trim();
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query); // Always sync input with URL param
    setError(null);
    setSongResults([]);
    setPostResults([]);

    if (!query) {
      setError('Please enter a search term.');
      return;
    }

    const fetchSearchResults = async () => {
      // Fetch songs
      const { data: songData, error: songError } = await supabase
        .from('songs')
        .select('id, title, composer, permalink, is_public, downloads, audio_url, created_at')
        .or(`title.ilike.%${query}%,composer.ilike.%${query}%`)
        .eq('is_public', true)
        .limit(12);

      // Fetch posts
      const { data: postData, error: postError } = await supabase
        .from('blog_posts')
        .select('id, title, permalink')
        .ilike('title', `%${query}%`)
        .limit(8);

      if (songError) {
        setError('Failed to search songs: ' + songError.message);
        setSongResults([]);
      } else {
        setSongResults(songData || []);
      }
      if (postError) {
        setError('Failed to search posts: ' + postError.message);
        setPostResults([]);
      } else {
        setPostResults(postData || []);
      }
    };

    fetchSearchResults();
  }, [query]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;

    // Fire GA4 event for search
    if (window.gtag) {
      window.gtag('event', 'site_search', {
        event_category: 'Search Page',
        event_label: trimmed,
        search_term: trimmed,
        value: 1,
      });
    }

    navigate(`/search?query=${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-green-100 py-8 px-2">
      <div className="max-w-4xl mx-auto w-full">

        {/* Search Bar */}
        <form
          onSubmit={handleSearchSubmit}
          className="flex items-center justify-center mb-8 w-full max-w-2xl mx-auto"
        >
          <input
            type="text"
            className="w-full px-4 py-3 rounded-xl border border-green-500 bg-white focus:bg-white focus:ring-2 focus:ring-green-400 text-green-900 text-lg shadow font-medium outline-none"
            placeholder="Search songs or blog posts..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            className="ml-2 px-6 py-2 rounded-full bg-gradient-to-r from-green-700 via-green-500 to-green-400 text-white font-bold shadow"
          >
            Search
          </button>
        </form>

        <h1 className="text-2xl sm:text-3xl font-bold text-green-800 text-center mb-6">
          Search Results for <span className="text-green-600">&ldquo;{query || '…'}&rdquo;</span>
        </h1>

        {error && (
          <div className="text-red-700 text-center bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5 shadow">
            {error}
          </div>
        )}

        {!error && (
          <>
            {/* SONGS SECTION */}
            <section className="mb-10">
              <h2 className="text-xl sm:text-2xl font-semibold text-green-800 text-center mb-4">
                Songs
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {songResults.length > 0 ? (
                  songResults.map((song) => (
                    <Link
                      to={`/song/${song.permalink || song.id}`}
                      key={song.id}
                      className={`
                        flex flex-col justify-between
                        rounded-2xl border border-green-200 shadow bg-white
                        overflow-hidden cursor-pointer
                        transition-colors duration-150 hover:border-green-500
                      `}
                      onClick={() => {
                        if (window.gtag) {
                          window.gtag('event', 'search_result_click', {
                            event_category: 'Search Song Result',
                            event_label: song?.title || 'Unknown Song',
                            song_id: song?.id,
                            song_permalink: song?.permalink,
                            value: 1,
                          });
                        }
                      }}
                    >
                      <div className="relative z-10 p-4 pb-3">
                        <h3 className="font-bold text-lg md:text-xl text-green-900 mb-1 text-center">
                          {song.title}
                        </h3>
                        <p className="text-green-700 text-sm mb-2 text-center">
                          {song.composer || 'Unknown Composer'}
                        </p>
                        {song.audio_url && (
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <span
                              className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-200"
                            >
                              <span className="mr-1">Audio Available</span>
                              {/* Headphone Icon */}
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                <path d="M4 13v-2a8 8 0 1 1 16 0v2" stroke="#22c55e" strokeWidth="1.5" fill="none"/>
                                <rect x="3" y="13" width="4" height="6" rx="2" fill="#22c55e" />
                                <rect x="17" y="13" width="4" height="6" rx="2" fill="#22c55e" />
                              </svg>
                            </span>
                          </div>
                        )}
                        <p className="text-green-700/80 text-xs mb-1 text-center">
                          Downloaded <b>{song.downloads || 0}</b> times
                        </p>
                        <p className="text-green-700/60 text-xs text-center">
                          Added {song.created_at
                            ? new Date(song.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Unknown Date'}
                        </p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="col-span-full text-green-500 text-center py-5">
                    No matching songs found.
                  </p>
                )}
              </div>
            </section>

            {/* BLOG POSTS SECTION */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-green-800 text-center mb-4">
                Blog Posts
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {postResults.length > 0 ? (
                  postResults.map((post) => (
                    <Link
                      to={`/blog/${post.permalink || `post-${post.id}`}`}
                      key={post.id}
                      className={`
                        flex flex-col justify-between
                        rounded-2xl border border-green-200 shadow bg-white
                        overflow-hidden cursor-pointer p-4
                        transition-colors duration-150 hover:border-green-500
                      `}
                      onClick={() => {
                        if (window.gtag) {
                          window.gtag('event', 'search_result_click', {
                            event_category: 'Search Blog Result',
                            event_label: post?.title || 'Unknown Blog',
                            blog_id: post?.id,
                            blog_permalink: post?.permalink,
                            value: 1,
                          });
                        }
                      }}
                    >
                      <h3 className="font-bold text-lg md:text-xl text-green-900 mb-2 text-center">
                        {post.title}
                      </h3>
                    </Link>
                  ))
                ) : (
                  <p className="col-span-full text-green-500 text-center py-5">
                    No matching blog posts found.
                  </p>
                )}
              </div>
            </section>
          </>
        )}

        <div className="flex justify-center mt-8">
          <Link
            to="/"
            className="
              px-7 py-2 rounded-full font-bold
              bg-gradient-to-r from-green-700 via-green-500 to-green-400
              text-white shadow text-base
            "
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Search;
